import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import useTranslations from "@/shared/lang/useTranslations";

export interface OrgSelectorProps {
  /** Deposit-accounts corridor namespace the labels come from (both share the same org_* keys). */
  i18nNamespace?: "depositClabes" | "depositCvus";
  organizations: Organization[];
  isLoading: boolean;
  value: string | null;
  onValueChange: (organizationId: string | null) => void;
}

/**
 * Searchable organization selector for the deposit-accounts views. Client-side filtering over the loaded
 * catalog (see `useOrganizationsCatalog` for the size rationale). The label carries name + email so ops can
 * disambiguate same-named orgs.
 */
export function OrgSelector({
  i18nNamespace = "depositClabes",
  organizations,
  isLoading,
  value,
  onValueChange,
}: OrgSelectorProps) {
  const { t } = useTranslations(i18nNamespace);

  const options = organizations.map((org) => ({
    value: org.id,
    label: org.email ? `${org.name} · ${org.email}` : org.name,
  }));

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor="deposit-accounts-org"
        className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
      >
        {t("org_label")}
      </label>
      <ColocaCombobox
        id="deposit-accounts-org"
        value={value}
        onValueChange={onValueChange}
        options={options}
        disabled={isLoading}
        placeholder={isLoading ? t("org_loading") : t("org_placeholder")}
        emptyMessage={t("org_empty")}
        aria-label={t("org_label")}
      />
    </div>
  );
}
